import React, { useEffect, useRef, useState } from "react";
import { Send, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import customerApi from "./customerApi";

/* HQ , Contact Heritage. A real two-way thread: the customer writes here,
   Heritage replies from the admin Customers hub, and it appears below. */
const AUBERGINE = "var(--hq-deep, #5A4565)";
const PLUM = "var(--hq-accent, #6F4A7A)";
const PARCH = "#D8C9A3";

export default function HQContactHeritage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [priority, setPriority] = useState("normal");
  const [orderId, setOrderId] = useState("");
  const [orders, setOrders] = useState([]);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const r = await customerApi.get("/customer/messages");
      setMessages(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error("Couldn't load your messages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    customerApi.get("/customer/orders")
      .then((r) => setOrders(r.data?.console || []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const r = await customerApi.post("/customer/messages", {
        body: text, priority, order_id: orderId || null,
      });
      setMessages((m) => [...m, r.data]);
      setBody(""); setPriority("normal"); setOrderId("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send, please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="hq-contact" className="space-y-5" style={{ color: AUBERGINE }}>
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ Heritage Headquarters</div>
        <h1 className="font-serif text-3xl mt-1">Contact Heritage</h1>
        <p className="text-sm mt-1 opacity-80">
          Write to us here, replies from Heritage appear right in this thread, and we'll email you when we've answered.
        </p>
      </header>

      <section className="bg-white border rounded-md p-4" style={{ borderColor: PARCH }}>
        {loading ? (
          <div className="inline-flex items-center gap-2 text-sm opacity-70"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-10 text-sm opacity-70" data-testid="hq-contact-empty">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No messages yet. Say kia ora below.
          </div>
        ) : (
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="hq-contact-thread">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}>
                <div
                  className="max-w-[80%] rounded-lg px-3.5 py-2.5 text-sm whitespace-pre-wrap"
                  style={m.sender === "customer"
                    ? { backgroundColor: PLUM, color: "white" }
                    : { backgroundColor: "#F6F0E4", color: AUBERGINE, border: `1px solid ${PARCH}` }}
                  data-testid={`hq-msg-${m.id}`}
                >
                  <div className="text-[9px] tracking-[0.25em] uppercase font-semibold opacity-70 mb-1">
                    {m.sender === "customer" ? "You" : "Heritage"} · {m.created_at ? new Date(m.created_at).toLocaleDateString("en-NZ") : ""}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-1 empty:hidden">
                    {m.priority === "urgent" && <span className="px-1.5 py-0.5 rounded-full text-[8px] tracking-[0.2em] uppercase font-bold bg-red-700 text-white">Urgent</span>}
                    {m.priority === "high" && <span className="px-1.5 py-0.5 rounded-full text-[8px] tracking-[0.2em] uppercase font-bold bg-amber-500 text-white">Important</span>}
                    {m.order_id && <span className="px-1.5 py-0.5 rounded-full text-[8px] tracking-[0.15em] uppercase font-semibold bg-black/10">Order #{String(m.order_id).slice(0, 8)}</span>}
                  </div>
                  {m.body}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-[9px] tracking-[0.3em] uppercase font-semibold opacity-60">How urgent?</span>
          {[["normal", "General"], ["high", "Important"], ["urgent", "Urgent"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setPriority(v)} data-testid={`hq-priority-${v}`}
              className="px-2.5 py-1 rounded-full border text-[10px] tracking-[0.15em] uppercase font-semibold transition-colors"
              style={priority === v
                ? { backgroundColor: v === "urgent" ? "#B91C1C" : v === "high" ? "#D97706" : PLUM, borderColor: "transparent", color: "#fff" }
                : { borderColor: PARCH, color: AUBERGINE }}>
              {l}
            </button>
          ))}
          {orders.length > 0 && (
            <select value={orderId} onChange={(e) => setOrderId(e.target.value)} data-testid="hq-msg-order"
              className="text-xs border rounded px-2 py-1.5 bg-white max-w-[220px]" style={{ borderColor: PARCH }}>
              <option value="">Not about an order</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>
                  About {o.order_number ? `#${o.order_number}` : `#${String(o.id).slice(0, 8)}`} · ${Number(o.total || 0).toFixed(0)}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="mt-2 flex gap-2 items-end">
          <textarea
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your message to Heritage…"
            data-testid="hq-contact-message"
            className="flex-1 px-3 py-2 text-sm border rounded resize-none"
            style={{ borderColor: PARCH }}
          />
          <button
            type="button"
            onClick={send}
            disabled={sending || !body.trim()}
            data-testid="hq-contact-submit"
            className="px-4 py-2.5 rounded text-[11px] tracking-[0.25em] uppercase font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ backgroundColor: PLUM }}
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
          </button>
        </div>
      </section>
    </div>
  );
}
